/**
 * Exercise Similarity Utility
 * 
 * Provides fuzzy string matching to help coaches find similar exercises
 * and avoid creating duplicates with slightly different names.
 */

/**
 * Calculate Levenshtein distance between two strings
 * @param {string} a - First string
 * @param {string} b - Second string
 * @returns {number} - Edit distance between strings
 */
function levenshteinDistance(a, b) {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = Array(b.length + 1)
    .fill(null)
    .map(() => Array(a.length + 1).fill(null));

  for (let i = 0; i <= a.length; i++) {
    matrix[0][i] = i;
  }

  for (let j = 0; j <= b.length; j++) {
    matrix[j][0] = j;
  }

  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      if (a[i - 1] === b[j - 1]) {
        matrix[j][i] = matrix[j - 1][i - 1];
      } else {
        matrix[j][i] = Math.min(
          matrix[j - 1][i - 1] + 1, // substitution
          matrix[j][i - 1] + 1,     // insertion
          matrix[j - 1][i] + 1      // deletion
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

/**
 * Calculate similarity ratio between two strings
 * @param {string} a - First string
 * @param {string} b - Second string
 * @returns {number} - Similarity ratio (0-1, where 1 is identical)
 */
function similarityRatio(a, b) {
  const distance = levenshteinDistance(a.toLowerCase(), b.toLowerCase());
  const maxLength = Math.max(a.length, b.length);
  return maxLength === 0 ? 1 : (maxLength - distance) / maxLength;
}

/**
 * Normalize exercise name for better matching
 * @param {string} name - Exercise name
 * @returns {string} - Normalized name
 */
function normalizeExerciseName(name) {
  if (!name) return '';
  
  return name
    .toLowerCase()
    .trim()
    // Remove common prefixes/suffixes
    .replace(/^(the\s+|a\s+|an\s+)/i, '')
    .replace(/\s+(exercise|workout|movement)$/i, '')
    // Normalize common variations
    .replace(/\b(dumbbell|db)\b/gi, 'dumbbell')
    .replace(/\b(barbell|bb)\b/gi, 'barbell')
    .replace(/\b(pushup|push-up)\b/gi, 'push up')
    .replace(/\b(pullup|pull-up)\b/gi, 'pull up')
    .replace(/\b(situp|sit-up)\b/gi, 'sit up')
    .replace(/\b(setup|set-up)\b/gi, 'set up')
    // Remove extra whitespace
    .replace(/\s+/g, ' ')
    .trim();
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Rank exercises for autocomplete: exact → prefix → all tokens present (substring) → substring phrase
 * in name/description → whole-string fuzzy. More robust than prefix + Levenshtein-only scoring.
 *
 * @param {Array<object>} exercises - Candidate exercises ({ id, name, description?, ... })
 * @param {string} rawInputValue - User typed string
 * @param {object} opts
 * @param {number} [opts.maxResults=20]
 * @param {number} [opts.minScore=0.45] - Exclude weaker matches below this similarity score
 * @returns {{ suggestions: Array<object>, hasExactNameMatch: boolean }}
 */
export function buildExerciseAutocompleteSuggestions(
  exercises,
  rawInputValue,
  { maxResults = 20, minScore = 0.45 } = {},
) {
  if (
    !rawInputValue ||
    String(rawInputValue).trim().length === 0 ||
    !Array.isArray(exercises) ||
    exercises.length === 0
  ) {
    return { suggestions: [], hasExactNameMatch: false };
  }

  const searchPhrase = normalizeExerciseName(rawInputValue);
  if (!searchPhrase) {
    return { suggestions: [], hasExactNameMatch: false };
  }

  const tokens = searchPhrase.split(" ").filter(Boolean);
  /** @type {Array<{ exercise: object, score: number, matchKind: string }>} */
  const ranked = [];

  for (const exercise of exercises) {
    if (!exercise?.id) continue;

    const nameNorm = normalizeExerciseName(exercise.name || "");
    const descNorm = normalizeExerciseName(exercise.description || "");
    const haystack = `${nameNorm} ${descNorm}`.trim();

    let score = 0;
    /** @type {"exact" | "prefix" | "tokens" | "substring" | "fuzzy"} */
    let matchKind = "fuzzy";

    if (nameNorm === searchPhrase) {
      ranked.push({
        exercise,
        score: 1,
        matchKind: "exact",
      });
      continue;
    }

    // Prefix match on canonical name
    if (nameNorm.startsWith(searchPhrase)) {
      matchKind = "prefix";
      const ratio = similarityRatio(searchPhrase, nameNorm);
      score = Math.min(1, 0.92 + Math.min(0.08, ratio * 0.08));
    }

    // Multi-token AND: each token appears anywhere in name/description
    if (
      tokens.length > 1 &&
      tokens.every((t) => t.length > 0 && haystack.includes(t))
    ) {
      let tokenScore = 0.78;
      if (haystack.includes(searchPhrase)) tokenScore += 0.06;

      const wordBoundaryHits = tokens.filter((t) => {
        if (t.length < 2) return false;
        const re = new RegExp(`(^|[^a-z0-9])${escapeRegExp(t)}`);
        return re.test(nameNorm);
      }).length;
      tokenScore += Math.min(0.08, wordBoundaryHits * 0.025);

      if (tokenScore > score) {
        score = tokenScore;
        matchKind = "tokens";
      }
    }

    // Middle-of-string / description: contiguous phrase substring
    if (searchPhrase.length >= 2 && haystack.includes(searchPhrase)) {
      let subScore = 0.62;
      if (nameNorm.includes(searchPhrase)) {
        subScore = 0.68;
        const idx = nameNorm.indexOf(searchPhrase);
        subScore += Math.min(
          0.12,
          0.12 * (1 - idx / Math.max(nameNorm.length, 1)),
        );
      }

      if (searchPhrase.length >= 3) {
        try {
          const re = new RegExp(
            `(^|[^a-z0-9])${escapeRegExp(searchPhrase)}([^a-z0-9]|$)`,
          );
          if (re.test(nameNorm)) subScore += 0.07;
        } catch {
          // ignore malformed regex from unexpected input shapes
        }
      }

      if (subScore > score) {
        score = subScore;
        matchKind = "substring";
      }
    }

    // Whole-string fuzzy (typo / near match)
    const fuzzy = similarityRatio(searchPhrase, nameNorm);
    const fuzzyWeighted = fuzzy * 0.86;
    if (fuzzyWeighted > score && fuzzy >= minScore) {
      score = fuzzyWeighted;
      matchKind = "fuzzy";
    }

    if (score >= minScore) {
      ranked.push({ exercise, score, matchKind });
    }
  }

  ranked.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return (a.exercise.name || "").localeCompare(b.exercise.name || "");
  });

  const seen = new Set();
  const suggestions = [];
  let hasExactNameMatch = false;

  for (const row of ranked) {
    const id = row.exercise?.id;
    if (!id || seen.has(id)) continue;
    seen.add(id);

    suggestions.push({
      ...row.exercise,
      similarity: row.score,
      isSimilar: row.matchKind !== "exact",
    });

    if (row.matchKind === "exact") hasExactNameMatch = true;
    if (suggestions.length >= maxResults) break;
  }

  return { suggestions, hasExactNameMatch };
}

/**
 * Find similar exercises based on name similarity
 * @param {string} searchName - Name to search for
 * @param {Array} exercises - Array of exercise objects with 'name' property
 * @param {number} threshold - Similarity threshold (0-1, default 0.7)
 * @param {number} maxResults - Maximum number of results (default 5)
 * @returns {Array} - Array of similar exercises with similarity scores
 */
export function findSimilarExercises(searchName, exercises, threshold = 0.7, maxResults = 5) {
  if (!searchName || !exercises || exercises.length === 0) {
    return [];
  }

  const normalizedSearch = normalizeExerciseName(searchName);
  
  const similarities = exercises
    .map(exercise => {
      const normalizedExercise = normalizeExerciseName(exercise.name || '');
      const similarity = similarityRatio(normalizedSearch, normalizedExercise);
      
      // Bonus points for exact word matches
      const searchWords = normalizedSearch.split(' ');
      const exerciseWords = normalizedExercise.split(' ');
      const commonWords = searchWords.filter(word => 
        word.length > 2 && exerciseWords.includes(word)
      );
      const wordBonus = commonWords.length / Math.max(searchWords.length, 1) * 0.1;
      
      return {
        exercise,
        similarity: Math.min(similarity + wordBonus, 1),
        normalizedName: normalizedExercise
      };
    })
    .filter(item => item.similarity >= threshold)
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, maxResults);

  return similarities;
}

/**
 * Check if an exercise name is too similar to existing exercises
 * @param {string} newName - New exercise name to check
 * @param {Array} existingExercises - Array of existing exercises
 * @param {number} threshold - Similarity threshold (default 0.85 for duplicates)
 * @returns {Object} - Result with isDuplicate flag and similar exercises
 */
export function checkForDuplicates(newName, existingExercises, threshold = 0.85) {
  const similar = findSimilarExercises(newName, existingExercises, threshold, 3);
  
  return {
    isDuplicate: similar.length > 0,
    similarExercises: similar,
    suggestion: similar.length > 0 ? similar[0].exercise : null
  };
}

/**
 * Get exercise suggestions while typing
 * @deprecated Use findSimilarExercises() directly for better functionality with similarity scores
 * @param {string} input - Current input text
 * @param {Array} exercises - Array of available exercises
 * @param {number} minLength - Minimum input length to start suggesting (default 2)
 * @returns {Array} - Array of suggested exercises (without similarity scores)
 * 
 * NOTE: This function strips similarity scores from findSimilarExercises().
 * Consider using findSimilarExercises() directly if you need percentage match display.
 */
export function getExerciseSuggestions(input, exercises, minLength = 2) {
  if (!input || input.length < minLength) {
    return [];
  }

  // First try exact prefix matches
  const prefixMatches = exercises.filter(exercise =>
    exercise.name && exercise.name.toLowerCase().startsWith(input.toLowerCase())
  );

  // Then add fuzzy matches if we need more results
  const fuzzyMatches = findSimilarExercises(input, exercises, 0.6, 10)
    .filter(item => !prefixMatches.find(match => match.id === item.exercise.id))
    .map(item => item.exercise);

  return [...prefixMatches, ...fuzzyMatches].slice(0, 10);
}

export default {
  findSimilarExercises,
  checkForDuplicates,
  getExerciseSuggestions,
  buildExerciseAutocompleteSuggestions,
  normalizeExerciseName,
  similarityRatio,
};

